<template>
	<div class="page apps-page">
		<p class="apps-description sgnl-paragraph-m-regular sgnl-text-color-secondary">
			Examples of interactive web apps whose underlying computations run on the Signaloid Cloud Compute Engine
			(SCCE). In most cases, the apps are built by taking an existing legacy C/C++ application that has not been
			explicitly designed to perform uncertainty quantification, and pairing this computational kernel, running on
			the SCCE with its automated uncertainty tracking, with a user interface built using a template which
			Signaloid provides for free to customers. The complete source for these web apps are available upon request
			by contacting
			<a
				class="apps-email-link sgnl-text-color-brand"
				href="mailto: developer-support@signaloid.com"
				>developer-support@signaloid.com</a
			>.
		</p>
		<div class="card-grid">
			<v-card
				class="app-card sgnl-flex sgnl-flex-column"
				v-for="demo in activeDemos"
				:key="demo.title"
			>
				<v-img
					class="sgnl-align-end sgnl-text-color-inverse app-card-image"
					:src="demo.bannerImage"
					gradient="to top, rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.1)"
					cover
				>
					<v-card-title class="app-card-title sgnl-paragraph-m-medium sgnl-text-color-inverse">{{
						demo.title
					}}</v-card-title>
				</v-img>

				<div class="sgnl-flex sgnl-flex-wrap sgnl-p-12 sgnl-pb-0 sgnl-gap-4">
					<v-chip variant="outlined">
						{{ demo.atomicNetwork }}
					</v-chip>
					<v-chip variant="outlined">
						<img
							src="@/assets/icons/cores/core-chip-16.svg"
							width="16"
							height="16"
							alt="Core"
						/>
						{{ demo.requiredCore }}
					</v-chip>
				</div>

				<div
					class="app-card-text sgnl-paragraph-s-regular sgnl-p-12 sgnl-text-color-secondary"
					v-html="demo.description"
				/>

				<v-spacer />

				<div class="demo-card-actions sgnl-p-12 sgnl-pt-0">
					<v-btn
						v-if="demo.sourceRepoURL"
						variant="outlined"
						:disabled="repositoriesStore.isURLConnected(demo.sourceRepoURL)"
						@click="connectRepo(demo.sourceRepoURL)"
					>
						<template v-if="repositoriesStore.isURLConnected(demo.sourceRepoURL)">
							<Checkmark16 />
							Connected
						</template>
						<template v-else>
							<BookmarkAdd16 />
							Connect
						</template>
					</v-btn>
					<template v-if="demo.active">
						<v-btn
							variant="flat"
							color="primary"
							class="view-app-btn"
							:href="getUrlBasedOnEnv(demo)"
							target="_blank"
						>
							View App
							<ArrowUpRight16 />
						</v-btn>
					</template>
					<template v-else>
						<UpgradeButton btn-text="Upgrade To Access" />
					</template>
				</div>
			</v-card>
		</div>
	</div>
</template>
<script lang="ts">
/* eslint-disable vue/multi-word-component-names */
import { defineComponent } from "vue";
import UpgradeButton from "@/components/Common/UpgradeButton.vue";

// Carbon Icons
import ArrowUpRight16 from "@carbon/icons-vue/es/arrow--up-right/16";
import BookmarkAdd16 from "@carbon/icons-vue/es/bookmark--add/16";
import Checkmark16 from "@carbon/icons-vue/es/checkmark/16";

// Stores
import { mapState } from "pinia";
import { useCoresStore } from "@/stores/cores";
import { useUserStore } from "@/stores/user";
import { useRepositoriesStore } from "@/stores/repositories";

// Utilities
import { navigateToConnectRepo } from "@/js/repositoryUtilities";

// Types
import { groupCode, AccountGroupCode, compareTierCodes } from "@/js/tierconfig";

// Banner images
import bannerBrownHam from "@/assets/Apps/api-demo-brown-ham.jpg";
import bannerBme680 from "@/assets/Apps/api-demo-bme680.jpg";
import bannerMlx90640 from "@/assets/Apps/api-demo-mlx90640.jpg";
import bannerRetirement from "@/assets/Apps/api-demo-retirement-account.jpg";
import bannerFinance from "@/assets/Apps/api-demo-finance.jpg";
import bannerBattery from "@/assets/Apps/api-demo-battery-estimation.jpg";

type Demo = {
	title: string;
	description: string;
	requiredCore: string;
	requiredTiers: AccountGroupCode[];
	atomicNetwork: string;
	prodDemoUrl: string;
	bannerImage: string;
	active: boolean;
	sourceRepoURL?: string;
};

const demos: Demo[] = [
	{
		title: "Precipitate Dislocation Model from Brown and Ham",
		description:
			"The precipitate dislocation model from Brown and Ham provides metallurgists with insight into \
			a metal alloy’s strength. This application implements the model but treats the inputs as probability \
			distributions, and calculates the output cutting stress distribution using Signaloid’s uncertainty \
			quantification technology.",
		requiredCore: "C0Pro-XS+",
		requiredTiers: [
			"free-tier",
			"developer-tier",
			"academic-research-tier",
			"pro-tier",
			"enterprise-tier-s",
			"enterprise-tier-m",
			"enterprise-tier-l",
		],
		atomicNetwork: "Materials Modeling",
		prodDemoUrl: "https://app.signaloid.io/demo-free/brown-and-ham",
		bannerImage: bannerBrownHam,
		active: false,
		sourceRepoURL: "https://github.com/signaloid/Signaloid-Demo-Metallurgy-BrownHamModel",
	},
	{
		title: "Bosch BME680 Calibration Routines",
		description:
			"Sensors use calibration algorithms to convert transducer readings into physical parameters. \
			Signaloid’s technology lets you quantify how sensor noise and transducer measurement uncertainty \
			can impact your system’s calibrated outputs.",
		requiredCore: "C0Pro-S+",
		requiredTiers: [
			"free-tier",
			"developer-tier",
			"academic-research-tier",
			"pro-tier",
			"enterprise-tier-s",
			"enterprise-tier-m",
			"enterprise-tier-l",
		],
		atomicNetwork: "Sensor Manufacturing",
		prodDemoUrl: "https://app.signaloid.io/demo-free/bme680",
		bannerImage: bannerBme680,
		active: false,
		sourceRepoURL: "https://github.com/signaloid/Signaloid-Demo-Sensors-BME680ConversionRoutines",
	},
	{
		title: "IR Camera Calibration Routines",
		description:
			"This example allows you to model the output uncertainty of the temperature sensor array, \
			given an uncertainty of measurand emissivity, and taking into account the ADC quantization error of the sensor.",
		requiredCore: "C0Pro-XS",
		requiredTiers: [
			"free-tier",
			"developer-tier",
			"academic-research-tier",
			"pro-tier",
			"enterprise-tier-s",
			"enterprise-tier-m",
			"enterprise-tier-l",
		],
		atomicNetwork: "Sensor Manufacturing",
		prodDemoUrl: "https://app.signaloid.io/demo-free/mlx90640",
		bannerImage: bannerMlx90640,
		active: false,
		sourceRepoURL: "https://github.com/signaloid/Signaloid-Demo-Sensors-MLX90640ConversionRoutines",
	},
	{
		title: "Retirement Savings Modeler",
		description:
			"This example estimates the final balance of a retirement savings account by modeling tax rates, interest rates,\
			and other financial uncertainties as probability distributions.",
		requiredCore: "C0Pro-XS",
		requiredTiers: [
			"free-tier",
			"developer-tier",
			"academic-research-tier",
			"pro-tier",
			"enterprise-tier-s",
			"enterprise-tier-m",
			"enterprise-tier-l",
		],
		atomicNetwork: "Financial",
		prodDemoUrl: "https://app.signaloid.io/demo-free/tax-free-retirement",
		bannerImage: bannerRetirement,
		active: false,
		sourceRepoURL: "https://github.com/signaloid/Signaloid-Demo-Finance-InvestmentRetirementAccount",
	},
	{
		title: "Moonfire Ventures Portfolio Model",
		description:
			"In the ArXiv paper “Venture Capital Portfolio Construction” by Farina et al., they propose a model for the optimal \
			      size for a venture capital investment portfolio. This application implements this model, but replaces the Monte Carlo \
			      methods proposed in the original with Signaloid’s uncertainty-tracking technology.",
		requiredCore: "C0Pro-M",
		requiredTiers: [
			"free-tier",
			"developer-tier",
			"academic-research-tier",
			"pro-tier",
			"enterprise-tier-s",
			"enterprise-tier-m",
			"enterprise-tier-l",
		],
		atomicNetwork: "Financial",
		prodDemoUrl: "https://app.signaloid.io/demo-free/portfolio-modeling",
		bannerImage: bannerFinance,
		active: false,
		sourceRepoURL: "https://github.com/signaloid/Signaloid-Demo-Finance-MoonfireVentureCapitalPortfolioModeling",
	},
	// {
	// 	title: "Icetime FPGA Timing Analysis Tool",
	// 	description:
	// 		"An implementation of the Icetime FPGA Timing Analysis Tool with \
	// 				Component-Level Timing Uncertainties.",
	// 	demoUrl: "https://api-demo-edaa-00.signaloid.io/",
	// 	requiredCore: "C0-XL",
	// 	requiredTier: "enterprise-tier",
	// 	atomicNetwork: "EDA Applications",
	// 	bannerImage: require("@/assets/Apps/api-demo-icetime.jpg"),
	// 	active: false,
	// },
	{
		title: "Battery State of Charge Estimation",
		description:
			"The state of charge of a battery expresses its remaining capacity \
			as a percentage of its total capacity. Accurately estimating the state of \
			charge is crucial in battery-powered systems. This example models the state \
			of charge of a Panasonic CGR-17500 cell given a noisy voltage measurement.",
		requiredCore: "C0Pro-XL+",
		requiredTiers: [
			"free-tier",
			"developer-tier",
			"academic-research-tier",
			"pro-tier",
			"enterprise-tier-s",
			"enterprise-tier-m",
			"enterprise-tier-l",
		],
		atomicNetwork: "Energy Storage",
		prodDemoUrl: "https://app.signaloid.io/demo-free/battery-state",
		bannerImage: bannerBattery,
		active: false,
		sourceRepoURL: "https://github.com/signaloid/Signaloid-Demo-Batteries-StateOfChargeEstimation",
	},
	{
		title: "Value at Risk (VaR)",
		description:
			"Value at Risk (VaR) is a widely-used investment risk analysis tool for estimating \
			potential financial losses over a specific time period. This application computes \
			VaR deterministically in one calculation, and uses geometric Brownian motion to \
			model the asset price over time, based on the implementation in “Mathematical \
			Modeling And Computation In Finance” by Oosterlee and Grzelak.",
		requiredCore: "C0Pro-XS",
		requiredTiers: [
			"free-tier",
			"developer-tier",
			"academic-research-tier",
			"pro-tier",
			"enterprise-tier-s",
			"enterprise-tier-m",
			"enterprise-tier-l",
		],
		atomicNetwork: "Financial",
		prodDemoUrl: "https://app.signaloid.io/demo-free/value-at-risk",
		bannerImage: bannerFinance,
		active: false,
		sourceRepoURL:
			"https://github.com/signaloid/Signaloid-Demo-Finance-OosterleeGrzelakBookGeometricBrownianMotionExample",
	},
];

export default defineComponent({
	name: "Apps",
	components: { UpgradeButton, ArrowUpRight16, BookmarkAdd16, Checkmark16 },
	setup() {
		const coresStore = useCoresStore();
		const userStore = useUserStore();
		const repositoriesStore = useRepositoriesStore();
		return { coresStore, userStore, repositoriesStore };
	},
	methods: {
		getActive(requiredTiers: AccountGroupCode[]) {
			if (this.userSubscriptionTierCode == "") {
				return false;
			}
			const userTierCode = this.userSubscriptionTierCode as AccountGroupCode;
			return requiredTiers.some((tierCode) => compareTierCodes(userTierCode, tierCode));
		},
		connectRepo(sourceRepoURL: string) {
			navigateToConnectRepo(this.$router, sourceRepoURL);
		},
	},
	computed: {
		...mapState(useCoresStore, {
			coresDisabledByTierCoreList: "coresFilteredCoresByUserTier",
			coresSatisfyMinConfig: "coresSatisfyMinConfig",
		}),
		activeDemos() {
			const activeDemos: Demo[] = [];
			for (let i = 0; i < demos.length; i++) {
				const demo = demos[i];
				demo.active = this.getActive(demo.requiredTiers);
				activeDemos.push(demo);
			}
			return activeDemos;
		},
		userSubscriptionTierCode(): "" | AccountGroupCode {
			return groupCode(this.userStore.currentUserSubscriptions?.Tier ?? "");
		},
		getUrlBasedOnEnv() {
			return (demo: Demo) => {
				if (import.meta.env.PROD) {
					return demo.prodDemoUrl;
				}
				return demo.prodDemoUrl.replace("signaloid.io", "signaloid.dev");
			};
		},
	},
	created() {
		this.coresStore.getCoresFilteredCoresByUserTier();
		if (this.repositoriesStore.connectedRepositories === undefined) {
			this.repositoriesStore.fetchRemoteRepositories();
		}
	},
});
</script>

<style lang="scss" scoped>
.apps-page {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-16);
}

.apps-email-link {
	text-decoration: underline;
	white-space: nowrap;
}

.apps-divider {
	border-top: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
}

.app-card {
	overflow: hidden;
}

.app-card-image {
	height: 140px;
	min-height: 140px;
	max-height: 140px;
	flex-shrink: 0;
}

.view-app-btn {
	min-width: 0;
}

.app-card-title {
	word-break: break-word;
	white-space: normal;
}

.app-card-text {
	:deep(a) {
		color: var(--sgnl-brand-text);
		text-decoration: underline;
	}
}

.demo-card-actions {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: var(--sgnl-space-8);
}

.demo-card-actions :deep(.v-btn svg) {
	width: var(--sgnl-icon-16);
	height: var(--sgnl-icon-16);
	flex-shrink: 0;
}

@media (max-width: 599px) {
	.demo-card-actions {
		grid-template-columns: 1fr;
	}
}

.card-grid :deep(.v-chip.v-chip--variant-outlined),
.card-grid :deep(.v-chip.v-chip--variant-outlined .v-chip__content) {
	color: var(--sgnl-default-secondary-text);
	font-size: var(--sgnl-font-size-caption);
	font-weight: 400;
	line-height: var(--sgnl-font-line-height-caption);
}

.card-grid {
	display: grid;
	grid-gap: 1em;
	grid-template-columns: 1fr;
}

@media (min-width: map-get($grid-breakpoints,sm)) {
	.card-grid {
		grid-template-columns: repeat(2, 1fr);
	}
}

@media (min-width: map-get($grid-breakpoints,md)) {
	.card-grid {
		grid-template-columns: repeat(3, 1fr);
	}
}

@media (min-width: map-get($grid-breakpoints,lg)) {
	.card-grid {
		grid-template-columns: repeat(4, 1fr);
	}
}
</style>
